import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Global from '../../../../store/global-selectors';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { DeprecatedTabs } from 'ui-components/components/DEPRECATED/tabs';
import { DeprecatedTab } from 'ui-components/components/DEPRECATED/tabs/tab';
import { DeprecatedTabButton } from 'ui-components/components/DEPRECATED/tabs/tab-button';
import { DeprecatedTabContent } from 'ui-components/components/DEPRECATED/tabs/tab-content';
import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import ModalPortal from '../../../../components/modal-portal';
import AddFundsTab from './add-funds-tab';
import AutoFundsTab from './auto-funds-tab';
import { ADD_FUNDS, AUTO_FUNDS } from './constants';
import AddFundsModalContextProvider from './contexts/add-funds-modal-context-provider';
import { PhoneVerificationContextProvider } from '../../../../components/phone-verification/context';

import messages from './messages';
import style from './style.module.less';
import SubmitButton from './submit-button';
import { AUD } from '../../../../../src/constants/currency-codes';

const AddFundsModal = ({
  balance,
  autoFundConfig = emptyObject,
  defaultPurchaseConfig = emptyObject,
  onAddFunds = noop,
  onSaveDefaultFundingAmount = noop,
  onSaveAutoFunding = noop,
  onSuccess,
  onClose,
  allowDefaultPurchase = false,
  allowAutoFunding = false,
  initialTab = ADD_FUNDS,
}) => {
  const [submitting, setSubmitting] = useAsyncSafeState(false);
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const selectAddFunds = useCallback(() => setSelectedTab(ADD_FUNDS), [setSelectedTab]);
  const selectAutoFunds = useCallback(() => setSelectedTab(AUTO_FUNDS), [setSelectedTab]);
  const defaultSellerCurrencyCode = useSelector((state) => Global.selectSellerDefaultCurrency(state));
  const currencyCode = balance?.code || defaultSellerCurrencyCode;

  return (
    <AddFundsModalContextProvider
      autoFundConfig={autoFundConfig}
      currencyCode={currencyCode}
      defaultPurchaseConfig={defaultPurchaseConfig}
      onAddFunds={onAddFunds}
      onSaveAutoFunding={onSaveAutoFunding}
      onSaveDefaultFundingAmount={onSaveDefaultFundingAmount}
      onSuccess={onSuccess}
      setSubmitting={setSubmitting}
      submitting={submitting}
    >
      <ModalPortal containerId='carriers-page-add-funds-modal' onClose={onClose}>
        <Modal className={style['add-funds-modal']}>
          <ModalHeader closeModal={onClose}>
            <FormattedMessage {...messages.addFunds} />
          </ModalHeader>
          <ModalBody>
            <div>
              <DeprecatedTabs bodyClassName={style['body-override']} className={style.tabs} headerClassName={style['header-override']}>
                <DeprecatedTab selected={selectedTab === ADD_FUNDS}>
                  <DeprecatedTabButton
                    className={classnames({
                      [style.nonselected]: selectedTab !== ADD_FUNDS,
                    })}
                    disabled={submitting}
                    onClick={selectAddFunds}
                    theme='file-tab'
                  >
                    <FormattedMessage {...messages.addFundsTab} />
                  </DeprecatedTabButton>
                  <DeprecatedTabContent key={ADD_FUNDS}>
                    <PhoneVerificationContextProvider>
                      <AddFundsTab allowDefaultPurchase={allowDefaultPurchase} balance={balance} />
                    </PhoneVerificationContextProvider>
                  </DeprecatedTabContent>
                </DeprecatedTab>
                {allowAutoFunding && (
                  <DeprecatedTab selected={selectedTab === AUTO_FUNDS}>
                    <DeprecatedTabButton
                      className={classnames({
                        [style.nonselected]: selectedTab !== AUTO_FUNDS,
                      })}
                      disabled={submitting}
                      onClick={selectAutoFunds}
                      theme='file-tab'
                    >
                      <FormattedMessage {...messages.autoFundsTab} />
                    </DeprecatedTabButton>
                    <DeprecatedTabContent key={AUTO_FUNDS}>
                      <AutoFundsTab balance={balance} />
                    </DeprecatedTabContent>
                  </DeprecatedTab>
                )}
              </DeprecatedTabs>
              {balance?.code === AUD && (
                <div className={style['transaction-fee-message']}>
                  <FormattedMessage {...messages.transactionFeeAuOnly} />
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter className={style['add-funds-footer']}>
            <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancel} />
            </Button>
            <SubmitButton currencyCode={balance?.code} selectedTab={selectedTab} />
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </AddFundsModalContextProvider>
  );
};

AddFundsModal.propTypes = {
  balance: PropTypes.object.isRequired,
  autoFundConfig: PropTypes.object,
  defaultPurchaseConfig: PropTypes.object,
  onAddFunds: PropTypes.func,
  onSaveDefaultFundingAmount: PropTypes.func,
  onSaveAutoFunding: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  allowDefaultPurchase: PropTypes.bool,
  allowAutoFunding: PropTypes.bool,
  initialTab: PropTypes.string,
};

export default AddFundsModal;
